import { useState, useEffect } from "react";
import { Building2, Plus, Pencil, Trash2, ChevronDown, ChevronRight, X, Search, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { MultiSelect } from "@/components/ui/multi-select";

export default function ManageOrganizations() {
  const { user } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrg, setExpandedOrg] = useState(null);
  const [orgUsers, setOrgUsers] = useState({});
  const [paOptions, setPaOptions] = useState([]);

  // Modal states
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [targetOrgId, setTargetOrgId] = useState(null);
  const [savingUser, setSavingUser] = useState(false);

  // Form states
  const [orgForm, setOrgForm] = useState({ name: "", assigned_pas: [] });
  const [userForm, setUserForm] = useState({ email: "", name: "", role: "viewer" });
  const [searchQuery, setSearchQuery] = useState("");

  const isInternal = user?.user_type === "internal";
  const isOrgAdmin = user?.user_type === "org" && user?.org_role === "admin";

  useEffect(() => {
    fetchOrganizations();
    if (user?.user_type === "internal") fetchPAOptions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchOrganizations = async () => {
    try {
      const res = await axios.get(`${API}/organizations`, { withCredentials: true });
      setOrganizations(res.data);
    } catch (err) {
      toast.error("Failed to load organizations");
    } finally {
      setLoading(false);
    }
  };

  const fetchPAOptions = async () => {
    try {
      const res = await axios.get(`${API}/organizations/pa-options`, { withCredentials: true });
      setPaOptions(res.data.pas || []);
    } catch (err) {
      console.error("Failed to fetch PA options:", err);
    }
  };

  const fetchOrgUsers = async (orgId) => {
    try {
      const res = await axios.get(`${API}/organizations/${orgId}/users`, { withCredentials: true });
      setOrgUsers(prev => ({ ...prev, [orgId]: res.data }));
    } catch (err) {
      toast.error("Failed to load users");
    }
  };

  const toggleExpand = (orgId) => {
    if (expandedOrg === orgId) {
      setExpandedOrg(null);
    } else {
      setExpandedOrg(orgId);
      if (!orgUsers[orgId]) fetchOrgUsers(orgId);
    }
  };

  // Org CRUD
  const handleSaveOrg = async () => {
    try {
      if (editingOrg) {
        await axios.put(`${API}/organizations/${editingOrg.org_id}`, orgForm, { withCredentials: true });
        toast.success("Organization updated");
      } else {
        await axios.post(`${API}/organizations`, orgForm, { withCredentials: true });
        toast.success("Organization created");
      }
      setShowOrgModal(false);
      setEditingOrg(null);
      setOrgForm({ name: "", assigned_pas: [] });
      fetchOrganizations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save organization");
    }
  };

  const handleDeleteOrg = async (orgId) => {
    if (!window.confirm("Delete this organization and all its users?")) return;
    try {
      await axios.delete(`${API}/organizations/${orgId}`, { withCredentials: true });
      toast.success("Organization deleted");
      fetchOrganizations();
    } catch (err) {
      toast.error("Failed to delete organization");
    }
  };

  // User CRUD
  const handleSaveUser = async () => {
    setSavingUser(true);
    try {
      if (editingUser) {
        const payload = { name: userForm.name, role: userForm.role };
        await axios.put(`${API}/organizations/${targetOrgId}/users/${editingUser.user_id}`, payload, { withCredentials: true });
        toast.success("User updated");
      } else {
        const res = await axios.post(`${API}/organizations/${targetOrgId}/users`, userForm, { withCredentials: true });
        toast.success(res.data.message || "Invite sent");
      }
      setShowUserModal(false);
      setEditingUser(null);
      setUserForm({ email: "", name: "", role: "viewer" });
      fetchOrgUsers(targetOrgId);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(Array.isArray(detail) ? "Please enter a valid email" : detail || "Failed to save user");
    } finally {
      setSavingUser(false);
    }
  };

  const handleResendInvite = async (orgId, u) => {
    if (!window.confirm(`Send ${u.email} a new temporary password? Their current password will stop working.`)) return;
    try {
      const res = await axios.post(`${API}/organizations/${orgId}/users/${u.user_id}/resend-invite`, {}, { withCredentials: true });
      toast.success(res.data.message);
      fetchOrgUsers(orgId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resend invite");
    }
  };

  const handleDeleteUser = async (orgId, userId) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await axios.delete(`${API}/organizations/${orgId}/users/${userId}`, { withCredentials: true });
      toast.success("User deleted");
      fetchOrgUsers(orgId);
    } catch (err) {
      toast.error("Failed to delete user");
    }
  };

  const filteredOrgs = organizations.filter(org =>
    !searchQuery || org.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {isInternal ? "Organizations" : "Manage Users"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isInternal ? "Manage external organizations and their PA access" : `Manage users for your organization`}
          </p>
        </div>
        {isInternal && (
          <Button
            onClick={() => { setEditingOrg(null); setOrgForm({ name: "", assigned_pas: [] }); setShowOrgModal(true); }}
            className="bg-[#006994] hover:bg-[#005577] text-white"
            data-testid="add-org-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Organization
          </Button>
        )}
      </div>

      {/* Search */}
      {isInternal && (
        <div className="relative mb-4 max-w-sm">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search organizations..."
            className="pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006994]/20 focus:border-[#006994] w-full"
          />
        </div>
      )}

      {/* Org List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin h-8 w-8 border-2 border-[#006994] border-t-transparent rounded-full" />
        </div>
      ) : filteredOrgs.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-12 text-center">
          <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No organizations yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredOrgs.map(org => (
            <div key={org.org_id} className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
              {/* Org Header */}
              <div
                className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-slate-50/50 transition-colors"
                onClick={() => toggleExpand(org.org_id)}
                data-testid={`org-row-${org.org_id}`}
              >
                <div className="flex items-center gap-3">
                  {expandedOrg === org.org_id ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                  <Building2 className="w-5 h-5 text-[#006994]" />
                  <div>
                    <p className="font-semibold text-slate-900">{org.name}</p>
                    <p className="text-xs text-slate-500">{org.assigned_pas?.length || 0} PAs assigned &bull; {org.user_count || 0} users</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${org.status === "active" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                    {org.status || "active"}
                  </span>
                  {isInternal && (
                    <>
                      <Button
                        size="sm" variant="ghost"
                        onClick={(e) => { e.stopPropagation(); setEditingOrg(org); setOrgForm({ name: org.name, assigned_pas: org.assigned_pas || [] }); setShowOrgModal(true); }}
                      >
                        <Pencil className="w-4 h-4 text-slate-400" />
                      </Button>
                      <Button
                        size="sm" variant="ghost"
                        onClick={(e) => { e.stopPropagation(); handleDeleteOrg(org.org_id); }}
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Expanded: PA List + Users */}
              {expandedOrg === org.org_id && (
                <div className="border-t border-slate-100 px-5 py-4">
                  {/* Assigned PAs */}
                  <div className="mb-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Assigned PAs</p>
                    <div className="flex flex-wrap gap-2">
                      {(org.assigned_pas || []).map(pa => (
                        <span key={pa} className="text-xs bg-[#006994]/10 text-[#006994] px-2.5 py-1 rounded-full font-medium">{pa}</span>
                      ))}
                      {(!org.assigned_pas || org.assigned_pas.length === 0) && (
                        <span className="text-xs text-slate-400">No PAs assigned</span>
                      )}
                    </div>
                  </div>

                  {/* Users */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Users</p>
                      {(isInternal || isOrgAdmin) && (
                        <Button
                          size="sm" variant="outline"
                          onClick={() => { setTargetOrgId(org.org_id); setEditingUser(null); setUserForm({ email: "", name: "", role: "viewer" }); setShowUserModal(true); }}
                          className="text-xs" data-testid={`add-user-btn-${org.org_id}`}
                        >
                          <Plus className="w-3 h-3 mr-1" /> Add User
                        </Button>
                      )}
                    </div>
                    {!orgUsers[org.org_id] ? (
                      <div className="text-center py-4">
                        <div className="animate-spin h-5 w-5 border-2 border-[#006994] border-t-transparent rounded-full mx-auto" />
                      </div>
                    ) : orgUsers[org.org_id].length === 0 ? (
                      <p className="text-xs text-slate-400 py-2">No users yet</p>
                    ) : (
                      <div className="space-y-2">
                        {orgUsers[org.org_id].map(u => (
                          <div key={u.user_id} className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-[#006994]/10 flex items-center justify-center text-[#006994] text-sm font-medium">
                                {u.name?.charAt(0) || "U"}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-slate-800">{u.name}</p>
                                <p className="text-xs text-slate-500">{u.email}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {u.must_change_password && (
                                <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700" data-testid={`invite-pending-${u.user_id}`}>
                                  Invite pending
                                </span>
                              )}
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${u.role === "admin" ? "bg-orange-100 text-orange-700" : "bg-blue-100 text-blue-700"}`}>
                                {u.role}
                              </span>
                              {(isInternal || isOrgAdmin) && (
                                <>
                                  <Button size="sm" variant="ghost" onClick={() => { setTargetOrgId(org.org_id); setEditingUser(u); setUserForm({ email: u.email, name: u.name, role: u.role }); setShowUserModal(true); }} data-testid={`edit-user-${u.user_id}`}>
                                    <Pencil className="w-3.5 h-3.5 text-slate-400" />
                                  </Button>
                                  {u.user_id !== user?.user_id && (
                                  <Button size="sm" variant="ghost" title="Resend invite" onClick={() => handleResendInvite(org.org_id, u)} data-testid={`resend-invite-${u.user_id}`}>
                                    <Send className="w-3.5 h-3.5 text-[#006994]" />
                                  </Button>
                                  )}
                                  <Button size="sm" variant="ghost" onClick={() => handleDeleteUser(org.org_id, u.user_id)} data-testid={`delete-user-${u.user_id}`}>
                                    <Trash2 className="w-3.5 h-3.5 text-red-400" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Org Modal */}
      {showOrgModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-slate-900">{editingOrg ? "Edit Organization" : "New Organization"}</h2>
              <button onClick={() => setShowOrgModal(false)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1.5 block">Organization Name</label>
                <input
                  type="text" value={orgForm.name} onChange={(e) => setOrgForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g., ATMUP SAL"
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006994]/20 focus:border-[#006994]"
                  data-testid="org-name-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1.5 block">Assigned PAs</label>
                <MultiSelect
                  options={paOptions}
                  selected={orgForm.assigned_pas}
                  onChange={(val) => setOrgForm(f => ({ ...f, assigned_pas: val }))}
                  placeholder="Select PAs..."
                  searchPlaceholder="Search PAs..."
                  showSelectAll={false}
                  testId="org-pa-multiselect"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowOrgModal(false)}>Cancel</Button>
              <Button onClick={handleSaveOrg} className="bg-[#006994] hover:bg-[#005577] text-white" disabled={!orgForm.name} data-testid="save-org-btn">
                {editingOrg ? "Update" : "Create"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* User Modal */}
      {showUserModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-slate-900">{editingUser ? "Edit User" : "Add User"}</h2>
              <button onClick={() => setShowUserModal(false)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1.5 block">Name</label>
                <input
                  type="text" value={userForm.name} onChange={(e) => setUserForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Full name" data-testid="user-name-input"
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006994]/20 focus:border-[#006994]"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1.5 block">Email</label>
                <input
                  type="email" value={userForm.email} onChange={(e) => setUserForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="user@org.com" disabled={!!editingUser} data-testid="user-email-input"
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006994]/20 focus:border-[#006994] disabled:bg-slate-50"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1.5 block">Role</label>
                <select
                  value={userForm.role} onChange={(e) => setUserForm(f => ({ ...f, role: e.target.value }))}
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006994]/20 focus:border-[#006994]"
                >
                  <option value="viewer">Viewer (Read-only)</option>
                  <option value="admin">Admin (Can manage users)</option>
                </select>
              </div>
              {!editingUser && (
                <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-100 rounded-lg" data-testid="invite-info-note">
                  <Send className="w-4 h-4 text-[#006994] mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-slate-600">A temporary password will be generated and emailed to this user. They'll set their own password on first sign-in. The invite is valid for 7 days.</p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowUserModal(false)}>Cancel</Button>
              <Button
                onClick={handleSaveUser}
                className="bg-[#006994] hover:bg-[#005577] text-white"
                disabled={!userForm.name || !userForm.email || savingUser}
                data-testid="save-user-btn"
              >
                {editingUser ? "Update" : savingUser ? "Sending invite..." : "Add & Send Invite"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
